import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { SEO } from "@/components/SEO";
import { 
  Check, 
  X, 
  Shield, 
  Key, 
  Server, 
  HelpCircle,
  Target,
  Loader2,
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription, SUBSCRIPTION_TIERS, type SubscriptionTier } from "@/contexts/SubscriptionContext";
import { toast } from "@/hooks/use-toast";

interface PricingTier {
  id: SubscriptionTier | "enterprise";
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  limitations?: string[];
  cta: string;
  popular?: boolean;
  enterprise?: boolean;
}

const pricingTiers: PricingTier[] = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    period: "forever",
    description: "Try Reqqy with limited usage",
    features: [
      "10 credits per month",
      "Quick clarity analysis",
      "Basic ambiguity detection",
      "Markdown export",
    ],
    limitations: [
      "No deep analysis",
      "No Jira integration",
      "No credit top-ups",
    ],
    cta: "Get Started",
  },
  {
    id: "solo",
    name: "Solo",
    price: "$29",
    period: "per month",
    description: "For individual product managers",
    features: [
      "100 credits per month",
      "Quick & deep analysis",
      "Full clarity reports",
      "CSV + Markdown export",
      "Credit top-ups available",
      "Email support",
    ],
    cta: "Start Free Trial",
  },
  {
    id: "startup",
    name: "Startup",
    price: "$99",
    period: "per month",
    description: "For growing product teams",
    features: [
      "500 credits per month",
      "5 team members",
      "Temporary mode",
      "Jira integration",
      "Credit top-ups available",
      "Priority support",
    ],
    cta: "Start Free Trial",
    popular: true,
  },
  {
    id: "business",
    name: "Business",
    price: "$299",
    period: "per month",
    description: "For established organizations",
    features: [
      "2,000 credits per month",
      "25 team members",
      "Temporary & standard modes",
      "Full Jira integration",
      "Credit top-ups available",
      "BYOK support",
      "SSO / SAML",
      "Dedicated support",
    ],
    cta: "Start Free Trial",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "Custom",
    period: "per year",
    description: "Zero-retention execution available",
    features: [
      "Custom credit allocation",
      "Unlimited team members",
      "Zero-retention execution",
      "On-premise deployment",
      "Custom integrations",
      "Dedicated CSM",
      "SLA guarantee",
      "Audit logs",
    ],
    cta: "Contact Sales",
    enterprise: true,
  },
];

const securityFaqs = [
  {
    question: "Is my data stored by default?",
    answer: "Yes, by default Reqqy stores your run history for your convenience. This allows you to review past analyses and track clarity improvements over time. You can switch to temporary mode for individual runs if you prefer ephemeral processing.",
  },
  {
    question: "What is temporary (ephemeral) mode?",
    answer: "Temporary mode deletes your run content after the session closes or after a TTL expires. Only run metrics (count, clarity scores) are retained for your usage dashboard. This is available on Startup plans and above.",
  },
  {
    question: "Do you support zero-retention execution?",
    answer: "Yes, Enterprise customers can enable zero-retention execution where data is processed and immediately discarded. This uses approved providers and ensures no customer content is ever stored on our servers.",
  },
  {
    question: "Do you train on customer data?",
    answer: "No. We do not train any models on customer data. We do not reuse customer content in any way. Your requirements and the improved versions remain yours.",
  },
  {
    question: "What is BYOK (Bring Your Own Key)?",
    answer: "BYOK allows you to use your own API keys for the underlying AI providers (OpenAI, Anthropic, or Azure). This means your data goes directly to your chosen provider, providing an additional layer of control. Available on Business and Enterprise plans.",
  },
  {
    question: "Is Reqqy compliant with regulations?",
    answer: "Yes, Reqqy is SOC 2 Type II certified and compliant with GDPR and CCPA. Enterprise customers can request our compliance documentation and sign a Data Processing Agreement (DPA).",
  },
];

export default function Pricing() {
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">("monthly");
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const { user } = useAuth();
  const { tier: currentTier, createCheckout, trialEligible, isTrial } = useSubscription();
  const navigate = useNavigate();

  const handleSelectPlan = async (tier: PricingTier) => {
    if (tier.enterprise) {
      navigate("/contact-sales");
      return;
    }

    if (tier.id === "free") {
      if (user) {
        navigate("/dashboard");
      } else {
        navigate("/auth?mode=signup");
      }
      return;
    }

    if (!user) {
      navigate("/auth?mode=signup");
      return;
    }

    if (tier.id === currentTier) {
      toast({
        title: "Already subscribed",
        description: `You're already on the ${tier.name} plan.`,
      });
      return;
    }

    setLoadingTier(tier.id);
    
    // Start trial if eligible, otherwise regular checkout
    const startTrial = trialEligible && !tier.enterprise && tier.price !== "$0";

    try {
      const checkoutUrl = await createCheckout(tier.id as SubscriptionTier, billingPeriod, startTrial);
      if (checkoutUrl) {
        window.open(checkoutUrl, "_blank");
      } else {
        toast({
          title: "Error",
          description: "Failed to create checkout session. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingTier(null);
    }
  };

  const getButtonText = (tier: PricingTier) => {
    if (loadingTier === tier.id) {
      return <Loader2 className="w-4 h-4 animate-spin" />;
    }
    
    if (user && tier.id === currentTier && !isTrial) {
      return "Current Plan";
    }

    if (user && tier.id === currentTier && isTrial) {
      return "On Trial";
    }
    
    if (!tier.enterprise && tier.price !== "$0" && trialEligible && !isTrial) {
      return "Start 7-Day Free Trial";
    }
    
    return tier.cta;
  };

  return (
    <PublicLayout>
      <SEO
        title="Pricing"
        description="Simple, transparent pricing for Reqqy. Start free with 10 credits, upgrade as your team grows."
        canonical="/pricing"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Reqqy Pricing",
          description: "Simple, transparent pricing for Reqqy requirements clarity engine.",
          mainEntity: {
            "@type": "ItemList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                item: {
                  "@type": "Product",
                  name: "Reqqy Free",
                  description: "10 credits per month for individual users",
                  offers: { "@type": "Offer", price: "0", priceCurrency: "USD", availability: "https://schema.org/InStock" },
                },
              },
              {
                "@type": "ListItem",
                position: 2,
                item: {
                  "@type": "Product",
                  name: "Reqqy Solo",
                  description: "100 credits per month for individual product managers",
                  offers: { "@type": "Offer", price: "29", priceCurrency: "USD", priceValidUntil: "2027-12-31", availability: "https://schema.org/InStock" },
                },
              },
              {
                "@type": "ListItem",
                position: 3,
                item: {
                  "@type": "Product",
                  name: "Reqqy Startup",
                  description: "500 credits per month for growing product teams",
                  offers: { "@type": "Offer", price: "99", priceCurrency: "USD", priceValidUntil: "2027-12-31", availability: "https://schema.org/InStock" },
                },
              },
              {
                "@type": "ListItem",
                position: 4,
                item: {
                  "@type": "Product",
                  name: "Reqqy Business",
                  description: "2000 credits per month for established organizations",
                  offers: { "@type": "Offer", price: "299", priceCurrency: "USD", priceValidUntil: "2027-12-31", availability: "https://schema.org/InStock" },
                },
              },
            ],
          },
        }}
      />
      {/* Hero */}
      <section className="py-20 md:py-32 gradient-hero">
        <div className="container px-4 md:px-6 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-6">
            Simple, transparent pricing
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
            Start free, scale as you grow. Reqqy identity remains the same across all tiers.
          </p>
          
          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 p-1 rounded-full bg-secondary">
            <button
              onClick={() => setBillingPeriod("monthly")}
              className={cn(
                "px-6 py-2 rounded-full text-sm font-medium transition-all",
                billingPeriod === "monthly" ? "bg-background shadow-sm" : "text-muted-foreground"
              )}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod("annual")}
              className={cn(
                "px-6 py-2 rounded-full text-sm font-medium transition-all",
                billingPeriod === "annual" ? "bg-background shadow-sm" : "text-muted-foreground"
              )}
            >
              Annual
              <span className="ml-2 text-xs text-primary">2 months free</span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 md:py-24 -mt-8">
        <div className="container px-4 md:px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {pricingTiers.map((tier) => (
              <div
                key={tier.name}
                className={cn(
                  "relative flex flex-col p-6 rounded-2xl transition-all",
                  tier.popular
                    ? "glass border-primary/50 glow-soft scale-105 z-10"
                    : "glass card-hover",
                  user && tier.id === currentTier && "ring-2 ring-primary"
                )}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full gradient-primary text-primary-foreground text-xs font-medium">
                    Most Popular
                  </div>
                )}

                {user && tier.id === currentTier && (
                  <div className="absolute -top-3 right-4 z-15 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                    Your Plan
                  </div>
                )}
                
                <div className="mb-6">
                  <h3 className="text-xl font-semibold mb-2">{tier.name}</h3>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-3xl font-display font-bold">
                      {tier.price === "Custom" 
                        ? tier.price 
                        : billingPeriod === "annual" && tier.price !== "$0"
                          ? `$${Math.round(parseInt(tier.price.slice(1)) * 10 / 12)}`
                          : tier.price
                      }
                    </span>
                    <span className="text-sm text-muted-foreground">
                      /{tier.price === "Custom" ? tier.period : "mo"}
                    </span>
                  </div>
                  {billingPeriod === "annual" && tier.price !== "$0" && tier.price !== "Custom" && (
                    <p className="text-xs text-primary font-medium">
                      ${parseInt(tier.price.slice(1)) * 10}/yr · billed annually
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground mt-1">{tier.description}</p>
                </div>

                <ul className="flex-1 space-y-3 mb-6">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                  {tier.limitations?.map((limitation) => (
                    <li key={limitation} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <X className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{limitation}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSelectPlan(tier)}
                  className={cn(
                    "w-full",
                    tier.popular || tier.enterprise
                      ? "gradient-primary text-primary-foreground hover:opacity-90"
                      : ""
                  )}
                  variant={tier.popular || tier.enterprise ? "default" : "outline"}
                  disabled={loadingTier === tier.id || (user && tier.id === currentTier)}
                >
                  {getButtonText(tier)}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Section */}
      <section className="py-16 md:py-24 bg-card/50">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                <Shield className="w-4 h-4" />
                Enterprise Ready
              </div>
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
                Security & Privacy
              </h2>
              <p className="text-lg text-muted-foreground">
                Reqqy takes your data privacy seriously
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="p-6 rounded-xl glass text-center">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Server className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">Data Storage</h3>
                <p className="text-sm text-muted-foreground">
                  Stored by default for convenience, temporary mode available
                </p>
              </div>
              <div className="p-6 rounded-xl glass text-center">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">No Training</h3>
                <p className="text-sm text-muted-foreground">
                  We do not train on or reuse customer data
                </p>
              </div>
              <div className="p-6 rounded-xl glass text-center">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Key className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">BYOK Support</h3>
                <p className="text-sm text-muted-foreground">
                  Use your own API keys for complete control
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security FAQ */}
      <section className="py-16 md:py-24">
        <div className="container px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
                Security & Privacy FAQ
              </h2>
              <p className="text-lg text-muted-foreground">
                Common questions about how Reqqy handles your data
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {securityFaqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="glass rounded-xl px-6 border-0"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-5">
                    <div className="flex items-center gap-3">
                      <HelpCircle className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="font-medium">{faq.question}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5 pl-8">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="py-16 md:py-24 bg-card/50">
        <div className="container px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-6">
            Ready to get started?
          </h2>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">
            Start your free trial today. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="gradient-primary text-primary-foreground h-12 px-8">
              <Link to="/auth?mode=signup">
                <Target className="w-4 h-4 mr-2" />
                Start Free Trial
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="h-12 px-8">
              <Link to="/contact-sales">Contact Sales</Link>
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
